/* eslint-disable no-underscore-dangle */
import React, { useRef, useCallback, useEffect, memo } from 'react';
import { VariableSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import ContentBlock from './ContentBlock';
import ButtonAddBlock from './ButtonAddBlock';

const DEFAULT_ROW_HEIGHT = 160;

const VirtualizedRow = memo(({ index, style, data }) => {
  const {
    blockIds,
    blocks,
    originalBlocks,
    renderContentBlockProps,
    setRowHeight,
    showAddButton,
    addButtonProps,
  } = data;
  const isAddButton = showAddButton && index === blockIds.length;
  const blockId = isAddButton ? null : blockIds[index];
  const rowRef = useRef(null);

  useEffect(() => {
    if (!rowRef.current) return undefined;
    const resizeObserver = new ResizeObserver(([entry]) => {
      if (entry) setRowHeight(index, entry.contentRect.height + 8);
    });
    resizeObserver.observe(rowRef.current);
    return () => resizeObserver.disconnect();
  }, [index, setRowHeight]);

  if (isAddButton) {
    return (
      <div style={style}>
        <ButtonAddBlock {...addButtonProps} />
      </div>
    );
  }

  return (
    <div style={style}>
      <div
        ref={rowRef}
        style={{ padding: '24px 24px 16px 0', boxSizing: 'border-box' }}
        key={blockId}
      >
        <ContentBlock
          {...{
            audioType: renderContentBlockProps.audioType,
            blockId,
            block: blocks[blockId],
            originalBlock: originalBlocks[blockId],
            disabledMoveUp: index === 0,
            characterExceed: renderContentBlockProps.characterExceed,
            isLastBlock: blockId === blockIds[blockIds.length - 1],
            onChangeCharacterExceed:
              renderContentBlockProps.onChangeCharacterExceed,
            onAddNewParagraphs: renderContentBlockProps.onAddNewParagraphs,
            onSelectParagraphId: renderContentBlockProps.onSelectParagraphId,
            onOpenDiscardedVoiceCloningDialog:
              renderContentBlockProps.onOpenDiscardedVoiceCloningDialog,
            isFocusing: renderContentBlockProps.focusingBlockId === blockId,
            onFocusingBlock: renderContentBlockProps.onFocusingBlock,
            onDragEnd: renderContentBlockProps.onDragEnd,
            onSaveProject: renderContentBlockProps.onSaveProject,
            voiceDefault: renderContentBlockProps.voiceDefault,
            isDownloading: renderContentBlockProps.isDownloading,
            onOpenOutOfCreditsDialog:
              renderContentBlockProps.onOpenOutOfCreditsDialog,
            checkValidCreateSynthesis:
              renderContentBlockProps.checkValidCreateSynthesis,
            isHighlight: renderContentBlockProps.currentBlockId === blockId,
            handlePlayAudio: renderContentBlockProps.handlePlayAudio,
            onOpenWrongSyntaxDialog:
              renderContentBlockProps.onOpenWrongSyntaxDialog,
            onOpenWrongSyntaxDialogV2:
              renderContentBlockProps.onOpenWrongSyntaxDialogV2,
            projectId: renderContentBlockProps.projectId,
            isPlayingBlock:
              renderContentBlockProps.isPlayingBlock &&
              renderContentBlockProps.currentPlayingBlockId === blockId,
            onConvertBlockToAudio:
              renderContentBlockProps.onConvertBlockToAudio,
            isPlaying: renderContentBlockProps.isPlaying,
            isDisabledDeleteBlock: blockIds.length === 1,
          }}
        />
      </div>
    </div>
  );
});

const VirtualizedContentList = ({
  blockIds,
  blocks,
  originalBlocks,
  renderContentBlockProps,
  containerHeight,
  showAddButton = false,
  addButtonProps = {},
}) => {
  const listRef = useRef(null);
  const rowHeights = useRef({});
  const prevBlockIds = useRef(blockIds);

  const setRowHeight = useCallback((index, size) => {
    const newSize = Math.max(size, 80);
    if (rowHeights.current[index] !== newSize) {
      rowHeights.current[index] = newSize;
      listRef.current?.resetAfterIndex(index, true);
    }
  }, []);

  const getRowHeight = useCallback(
    (index) => rowHeights.current[index] || DEFAULT_ROW_HEIGHT,
    [],
  );

  const scrollToBottom = useCallback(() => {
    if (listRef.current) {
      // Access the outer element
      // eslint-disable-next-line no-underscore-dangle
      const outerElement = listRef.current._outerRef;

      if (outerElement) {
        outerElement.scrollTop = outerElement.scrollHeight;
      }
    }
  }, [listRef]);

  const findInsertionIndex = (current, previous) => {
    for (let i = 0; i < current.length; i += 1) {
      if (current[i] !== previous[i]) return i;
    }
    return -1;
  };

  useEffect(() => {
    const current = blockIds.join(',');
    const previous = prevBlockIds.current.join(',');

    if (current === previous || !listRef.current) return;

    const prevLength = prevBlockIds.current.length;
    const currentLength = blockIds.length;

    const isAddingAtEnd =
      currentLength === prevLength + 1 &&
      prevBlockIds.current.every((id, idx) => id === blockIds[idx]);

    const isRemovingAtEnd =
      currentLength === prevLength - 1 &&
      blockIds.every((id, idx) => id === prevBlockIds.current[idx]);

    const insertionIndex =
      currentLength === prevLength + 1 && !isAddingAtEnd
        ? findInsertionIndex(blockIds, prevBlockIds.current)
        : -1;

    if (isAddingAtEnd) {
      listRef.current.resetAfterIndex(prevLength, false);
      setTimeout(scrollToBottom, 100);
    } else if (insertionIndex !== -1) {
      listRef.current.resetAfterIndex(insertionIndex, false);
    } else if (isRemovingAtEnd) {
      listRef.current.resetAfterIndex(currentLength, false);
    } else {
      listRef.current.resetAfterIndex(0, true);
    }

    prevBlockIds.current = [...blockIds];
  }, [blockIds.length, blockIds.join(','), scrollToBottom]);

  useEffect(() => {
    if (renderContentBlockProps.focusingBlockId && listRef.current) {
      const blockIndex = blockIds.indexOf(
        renderContentBlockProps.focusingBlockId,
      );
      if (blockIndex !== -1) {
        setTimeout(
          () => listRef.current.resetAfterIndex(blockIndex, true),
          100,
        );
      }
    }
  }, [renderContentBlockProps.focusingBlockId, blockIds]);

  // Handle scrolling to specific block (for error notifications, etc.)
  useEffect(() => {
    if (renderContentBlockProps.scrollToBlockId && listRef.current) {
      const blockIndex = blockIds.indexOf(
        renderContentBlockProps.scrollToBlockId,
      );
      if (blockIndex !== -1) {
        setTimeout(() => {
          listRef.current.scrollToItem(blockIndex, 'center');
        }, 100);
      }
    }
  }, [renderContentBlockProps.scrollToBlockId, blockIds]);

  return (
    <div
      style={{
        height: containerHeight || '70vh',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <AutoSizer>
        {({ height, width }) => (
          <List
            ref={listRef}
            height={height}
            width={width}
            itemCount={blockIds.length + (showAddButton ? 1 : 0)}
            itemSize={getRowHeight}
            overscanCount={10}
            itemData={{
              blockIds,
              setRowHeight,
              blocks,
              originalBlocks,
              renderContentBlockProps,
              showAddButton,
              addButtonProps,
            }}
            itemKey={(index) => blockIds[index]}
            threshold={200}
            style={{ scrollbarWidth: 'thin' }}
          >
            {VirtualizedRow}
          </List>
        )}
      </AutoSizer>
    </div>
  );
};

export default VirtualizedContentList;
